import React, { useState } from 'react';
import { useCachedQuery } from '../../hooks/useCachedQuery';
import { Set } from '../../types/mtg';
import { SetGrid } from '../../components/sets/SetGrid';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { Library, Percent, Package, Camera } from 'lucide-react';
import { SetsSkeleton } from '../../components/skeletons/SetsSkeleton';
import { GlobalPackProbabilities } from '../../components/stats/GlobalPackProbabilities';
import { Tabs, Button } from 'antd';
import { ProbabilityFilterOptions } from '../../types/pokemon';
import { AdSense } from '../../components/ads/AdSense';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { Helmet } from 'react-helmet-async';

export function Sets() {
  const { t, getPathForLang } = useI18n();
  const navigate = useNavigate();
  const { data: sets, loading, error } = useCachedQuery<Set[]>('sets', {
    orderBy: 'id'
  }, { single: false });
  const [activeTab, setActiveTab] = useState('sets');
  
  // Options de filtrage avancé pour les probabilités
  const [filterOptions, setFilterOptions] = useState<ProbabilityFilterOptions>({
    showMissing: true,
    showLessThanTwo: false,
    excludeTradable: false
  });

  if (loading) {
    return (
      <PageLayout>
        <Helmet>
          <title>{t('sets.loading')} | Pokemon TCG Pocket</title>
        </Helmet>
        <PageTitle title={t('sets.loading')} />
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-4"></div>
          <div className="h-24 bg-gray-100 rounded-lg mb-6"></div>
        </div>
        <SetsSkeleton />
      </PageLayout>
    );
  }

  if (error || !sets) {
    return (
      <PageLayout>
        <Helmet>
          <title>{t('sets.error.title')} | Pokemon TCG Pocket</title>
        </Helmet>
        <PageTitle title={t('sets.error.title')} />
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="mt-3 text-lg font-medium text-gray-900">{t('sets.error.title')}</h3>
          <p className="mt-2 text-sm text-gray-500">{t('sets.error.description')}</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
        </div>
      </PageLayout>
    );
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  return (
    <PageLayout>
      <Helmet>
        <title>{t('sets.seo.title')}</title>
        <meta name="description" content={t('sets.seo.description')} />
      </Helmet>
      <PageTitle title={t('sets.title')} />
      
      {/* En-tête de page amélioré et simplifié pour mobile */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 px-4 py-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 bg-indigo-100 p-2 rounded-lg">
                <Library className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                  {t('sets.header.title')}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  {t('sets.header.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="px-4 sm:px-6 py-4 sm:py-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-t border-indigo-100">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                  {t('sets.scanner.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-5">
                  {t('sets.scanner.description')}
                </p>
                <Button
                  type="primary"
                  onClick={() => navigate(getPathForLang('/card-scanner'))}
                  className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-12 sm:h-14 text-base sm:text-lg flex items-center justify-center gap-2 px-6 sm:px-8 rounded-xl shadow-lg shadow-indigo-500/20 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-200"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {t('sets.scanner.button')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Onglets améliorés */}
      <div className="mb-6">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-nav::before]:!border-b-0 [&_.ant-tabs-tab]:!bg-gray-50 [&_.ant-tabs-tab-active]:!bg-white [&_.ant-tabs-nav]:!px-0 [&_.ant-tabs-nav-wrap]:!mx-0"
          tabBarStyle={{ marginBottom: 0 }}
          tabBarGutter={0}
          type="card"
          items={[
            {
              key: 'sets',
              label: (
                <div className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 text-sm sm:text-base min-w-[80px] sm:min-w-0">
                  <Library className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{t('sets.tabs.sets')}</span>
                </div>
              ),
              children: (
                <div className="bg-white rounded-lg rounded-tl-none shadow p-3 sm:p-4 md:p-6">
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-100 text-xs sm:text-sm text-blue-800">
                    <p className="font-medium text-blue-900">{t('sets.help.title')}</p>
                    
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="flex items-center gap-2 bg-white/50 p-2 rounded-md">
                        <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">
                          <Library className="w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">{t('sets.help.collection.title')}</p>
                          <p className="text-gray-500 text-xs">{t('sets.help.collection.description')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 bg-white/50 p-2 rounded-md">
                        <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">
                          <Package className="w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">{t('sets.help.boosters.title')}</p>
                          <p className="text-gray-500 text-xs">{t('sets.help.boosters.description')}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 bg-white/50 p-2 rounded-md">
                        <div className="flex-shrink-0 w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">
                          <Camera className="w-3 h-3" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">{t('sets.help.scanner.title')}</p>
                          <p className="text-gray-500 text-xs">{t('sets.help.scanner.description')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Annonce AdSense pour l'onglet Séries */}
                  <div className="mb-6 bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex justify-center items-center">
                    <AdSense adSlot="1424794127" />
                  </div>
                  
                  <SetGrid sets={sets} />
                </div>
              ),
            },
            {
              key: 'stats',
              label: (
                <div className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 text-sm sm:text-base min-w-[80px] sm:min-w-0">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{t('sets.tabs.boosters')}</span>
                </div>
              ),
              children: (
                <div className="bg-white rounded-lg rounded-tl-none shadow p-3 sm:p-4 md:p-6">
                  {/* Annonce AdSense pour l'onglet Boosters */}
                  <div className="mb-6 bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex justify-center items-center">
                    <AdSense adSlot="1424794127" />
                  </div>
                  
                  <GlobalPackProbabilities excludeSets={['promo-a']} filterOptions={filterOptions} onFilterChange={setFilterOptions} />
                </div>
              ),
            },
          ]}
        />
      </div>
    </PageLayout>
  );
}